import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilTrash } from "@coreui/icons";
import { useCartStore, type StrategyCartItem } from "../../state/cartStore";
import { useTradingStrategies, type BillingCycle } from "../../api/hooks/useTradingStrategies";
import { useMembershipTiers } from "../../api/hooks/useMembershipTiers";
import {
  offersForLine,
  usableOffers,
  useUsableOffers,
  type UsableOfferDto,
} from "../../api/hooks/useOffers";
import { addOnDetailsPath, membershipDetailsPath, routePaths } from "../../routes/route-paths";
import { formatPriceUsd } from "../../lib/money";
import { StrategyIdentityBand } from "./components/StrategyIdentityBand";
import { StrategyMetaPills } from "./components/StrategyMetaPills";
import { StrategySideLabel } from "./components/StrategySideLabel";
import { MembershipBadge } from "./components/MembershipBadge";
import { CartLineOffer } from "./components/CartLineOffer";
import { countStrategiesByIds } from "./strategySides";
import { UpgradeCart } from "./UpgradeCart";
import { ExtendCart } from "./ExtendCart";
import { linesUsing, savingOnItem, savingOnLine } from "../../lib/offer-savings";
import { includedByMembershipsInCart } from "../../lib/cart-overlap";

const BILLING_CYCLES: BillingCycle[] = ["Monthly", "Yearly"];

/**
 * Which undecided lines take the single offer that fits them, and which are recorded as taking none.
 *
 * A capped offer goes to the lines it is worth most on. The ones that miss out are set to no offer
 * rather than left undecided, so they show the picker instead of being offered it again every render.
 */
function autoOfferDecisions(
  strategyItems: StrategyCartItem[],
  offers: UsableOfferDto[]
): Record<string, string | null> {
  const byOffer = new Map<string, { item: StrategyCartItem; offer: UsableOfferDto }[]>();

  for (const item of strategyItems.filter((i) => i.offerId === undefined)) {
    const fitting = offersForLine(offers, item.strategyId, item.billingCycle);
    if (fitting.length !== 1) {
      continue;
    }

    const candidates = byOffer.get(fitting[0].id) ?? [];
    candidates.push({ item, offer: fitting[0] });
    byOffer.set(fitting[0].id, candidates);
  }

  const decisions: Record<string, string | null> = {};
  for (const [offerId, candidates] of byOffer) {
    const { offer } = candidates[0];
    const room =
      offer.maxStrategies === null
        ? candidates.length
        : offer.maxStrategies - linesUsing(strategyItems, offerId).length;

    [...candidates]
      .sort((a, b) => savingOnLine(offer, b.item.priceUsd) - savingOnLine(offer, a.item.priceUsd))
      .forEach(({ item }, index) => {
        decisions[item.strategyId] = index < room ? offerId : null;
      });
  }

  return decisions;
}

export function CartCoreui() {
  const navigate = useNavigate();
  const strategyItems = useCartStore((s) => s.strategyItems);
  const membershipItems = useCartStore((s) => s.membershipItems);
  const addOnItems = useCartStore((s) => s.addOnItems);
  const upgrade = useCartStore((s) => s.upgrade);
  const extension = useCartStore((s) => s.extension);

  // The cart carries the name and the price it was added at, which is everything checkout needs.
  // The strategies are fetched for everything else the row states; until now that came from a
  // fixture array whose ids could never match a real strategy, so the line silently never rendered.
  const { data: strategies } = useTradingStrategies();
  const offers = usableOffers(useUsableOffers().data);
  const removeStrategy = useCartStore((s) => s.removeStrategy);
  const removeMembership = useCartStore((s) => s.removeMembership);
  const removeAddOn = useCartStore((s) => s.removeAddOn);
  const applyAutoOffers = useCartStore((s) => s.applyAutoOffers);
  const { data: tiers } = useMembershipTiers();
  const [pendingRemoval, setPendingRemoval] = useState<{ name: string; remove: () => void } | null>(null);

  const lineCount = strategyItems.length + membershipItems.length + addOnItems.length;

  const includedBy = includedByMembershipsInCart(membershipItems, tiers);
  const includedLines = strategyItems.filter((item) => includedBy.has(item.strategyId));

  const lineSaving = (item: StrategyCartItem) => savingOnItem(item, offers);

  // Shown as its own line rather than folded into the total, so a customer can see the offer did
  // something.
  const offersTotal = strategyItems.reduce((sum, item) => sum + lineSaving(item), 0);

  // Monthly and yearly lines bill on different cycles, so each cycle gets its own total. Added together
  // they made a figure the customer is never charged.
  const cycleTotals = BILLING_CYCLES.flatMap((cycle) => {
    const strategies = strategyItems.filter((item) => item.billingCycle === cycle);
    const memberships = membershipItems.filter((item) => item.billingCycle === cycle);
    const addOns = addOnItems.filter((item) => item.billingCycle === cycle);
    if (strategies.length + memberships.length + addOns.length === 0) {
      return [];
    }

    const total =
      strategies.reduce((sum, item) => sum + item.priceUsd - lineSaving(item), 0) +
      memberships.reduce((sum, item) => sum + item.priceUsd, 0) +
      addOns.reduce((sum, item) => sum + item.priceUsd, 0);
    return [{ cycle, total }];
  });

  // A line with exactly one offer that fits it has nothing to decide, so the cart decides for it —
  // for every line at once. A capped offer has to land where it is worth most, which no single line
  // can see; and lines deciding separately each saw the offer unused and took it, which put a
  // one-strategy offer on all of them.
  useEffect(() => {
    const decisions = autoOfferDecisions(strategyItems, offers);

    if (Object.keys(decisions).length > 0) {
      applyAutoOffers(decisions);
    }
  }, [strategyItems, offers, applyAutoOffers]);

  if (upgrade !== null) {
    return <UpgradeCart line={upgrade} />;
  }

  if (extension !== null) {
    return <ExtendCart line={extension} />;
  }

  const confirmRemove = () => {
    if (pendingRemoval) {
      pendingRemoval.remove();
      setPendingRemoval(null);
    }
  };

  return (
    <div>
      <h2>Your cart</h2>
      <p className="text-body-secondary mb-3">
        Review what you've selected and proceed to checkout.
      </p>

      <CCard>
        <CCardHeader>
          {lineCount === 0
            ? "Cart is empty"
            : `Cart items (${lineCount})`}
        </CCardHeader>
        {lineCount === 0 ? (
          <CCardBody>
            <p className="mb-0">
              No items yet.{" "}
              <Link to={routePaths.strategies}>Browse strategies</Link>
              .
            </p>
          </CCardBody>
        ) : (
          // p-0 on the body lets each row control its own padding so the
          // total band can sit flush against the bottom edge with its own bg.
          <CCardBody className="p-0">
            {membershipItems.map((item, idx) => {
              const tier = tiers?.find((candidate) => candidate.id === item.tierId);
              return (
                <div
                  key={item.tierId}
                  className={
                    idx < membershipItems.length - 1 || addOnItems.length > 0 || strategyItems.length > 0
                      ? "border-bottom"
                      : undefined
                  }
                >
                  <div className="strategy-band d-flex align-items-center gap-2 small fw-semibold">
                    {tier && (
                      <MembershipBadge
                        membership={{
                          badgeColour: tier.platform.badgeColour,
                          badgeLetters: tier.platform.badgeLetters,
                          platformName: tier.platform.name,
                        }}
                      />
                    )}
                    {tier ? `${tier.platform.name} · ${countStrategiesByIds(tier.tradingStrategyIds, strategies ?? [])} strategies` : "Membership"}
                  </div>
                  <div className="d-flex align-items-center justify-content-between gap-3 p-3">
                    <div className="flex-grow-1 min-width-0">
                      <Link to={membershipDetailsPath(item.tierId)} className="text-reset text-decoration-none">
                        <h5 className="mb-0">{item.name}</h5>
                      </Link>
                      <div className="d-flex flex-wrap gap-1 mt-2">
                        <CBadge color="secondary" shape="rounded-pill">
                          {item.billingCycle}
                        </CBadge>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-3 flex-shrink-0">
                      <span className="fs-5 fw-bold">
                        {formatPriceUsd(item.priceUsd)}
                        <span className="text-body-secondary fw-normal small">
                          {" "}
                          {item.billingCycle === "Yearly" ? "/yr" : "/mo"}
                        </span>
                      </span>
                      <CButton
                        color="danger"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setPendingRemoval({ name: item.name, remove: () => removeMembership(item.tierId) })
                        }
                        aria-label={`Remove ${item.name} from cart`}
                      >
                        <CIcon icon={cilTrash} />
                      </CButton>
                    </div>
                  </div>
                </div>
              );
            })}
            {addOnItems.map((item, idx) => (
              <div
                key={item.addOnId}
                className={idx < addOnItems.length - 1 || strategyItems.length > 0 ? "border-bottom" : undefined}
              >
                <div className="strategy-band d-flex align-items-center gap-2 small fw-semibold">Add-on</div>
                <div className="d-flex align-items-center justify-content-between gap-3 p-3">
                  <div className="flex-grow-1 min-width-0">
                    <Link to={addOnDetailsPath(item.addOnId)} className="text-reset text-decoration-none">
                      <h5 className="mb-0">{item.name}</h5>
                    </Link>
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      <CBadge color="secondary" shape="rounded-pill">
                        {item.billingCycle}
                      </CBadge>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-3 flex-shrink-0">
                    <span className="fs-5 fw-bold">
                      {formatPriceUsd(item.priceUsd)}
                      <span className="text-body-secondary fw-normal small">
                        {" "}
                        {item.billingCycle === "Yearly" ? "/yr" : "/mo"}
                      </span>
                    </span>
                    <CButton
                      color="danger"
                      variant="outline"
                      size="sm"
                      onClick={() => setPendingRemoval({ name: item.name, remove: () => removeAddOn(item.addOnId) })}
                      aria-label={`Remove ${item.name} from cart`}
                    >
                      <CIcon icon={cilTrash} />
                    </CButton>
                  </div>
                </div>
              </div>
            ))}
            {strategyItems.map((item, idx) => {
              const strategy = strategies?.find((s) => s.id === item.strategyId);
              return (
                <div
                  key={item.strategyId}
                  className={
                    idx < strategyItems.length - 1 ? "border-bottom" : undefined
                  }
                >
                  {/* The band the browse card carries, so the row reads as the thing the customer
                      clicked Add on. Drawn empty until the strategy arrives: it is a fixed height,
                      so the row does not jump once it does. */}
                  {strategy ? (
                    <StrategyIdentityBand strategy={strategy} />
                  ) : (
                    <div className="strategy-band" />
                  )}
                  <div className="d-flex align-items-center justify-content-between gap-3 p-3">
                    <div className="flex-grow-1 min-width-0">
                      <Link
                        to={`/strategies/${item.strategyId}`}
                        className="text-reset text-decoration-none"
                      >
                        <h5 className="mb-0">{item.name}</h5>
                      </Link>
                      {strategy && (
                        <StrategySideLabel direction={strategy.direction} className="small fw-semibold mt-1" />
                      )}
                      {strategy && (
                        <div className="d-flex flex-wrap gap-1 mt-2">
                          <StrategyMetaPills strategy={strategy} withRiskProfile withTimeframe={false} />
                        </div>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-3 flex-shrink-0">
                      <span className="fs-5 fw-bold">
                        {/* Struck through when an offer is on the line, so the row says what this
                            strategy costs rather than what it would have cost. */}
                        {lineSaving(item) > 0 && (
                          <span className="text-body-secondary fw-normal fs-6 text-decoration-line-through me-2">
                            {formatPriceUsd(item.priceUsd)}
                          </span>
                        )}
                        {formatPriceUsd(item.priceUsd - lineSaving(item))}
                        <span className="text-body-secondary fw-normal small">
                          {" "}
                          {item.billingCycle === "Yearly" ? "/yr" : "/mo"}
                        </span>
                      </span>
                      <CButton
                        color="danger"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setPendingRemoval({ name: item.name, remove: () => removeStrategy(item.strategyId) })
                        }
                        aria-label={`Remove ${item.name} from cart`}
                      >
                        <CIcon icon={cilTrash} />
                      </CButton>
                    </div>
                  </div>
                  <div className="px-3 pb-3">
                    <CartLineOffer item={item} offers={offers} />
                  </div>
                  {includedBy.has(item.strategyId) && (
                    <div className="tinted-warning mx-3 mb-3 p-2 rounded d-flex justify-content-between align-items-center gap-2 flex-wrap">
                      <span>
                        Included in the {includedBy.get(item.strategyId)} membership in your cart, so you don't need
                        it as well.
                      </span>
                      <CButton
                        color="secondary"
                        variant="outline"
                        size="sm"
                        onClick={() => removeStrategy(item.strategyId)}
                      >
                        Remove it
                      </CButton>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Totals as one block behind a single rule. Offers is the order's figure, not the last
                line's, and sitting flush under that line it read as that line's discount — on a cart
                with one discounted item and one at full price, against the wrong one. Earlier the band
                had a tinted bg-body-tertiary, but that reads brighter than expected in dark mode;
                typographic weight and the border keep both themes calm. */}
            <div className="border-top pb-3">
              {offersTotal > 0 && (
                <div className="d-flex align-items-center justify-content-between gap-3 px-3 pt-3">
                  <span className="text-body-secondary">Offers</span>
                  <span className="text-success fw-semibold">
                    -{formatPriceUsd(offersTotal)}
                  </span>
                </div>
              )}

              {cycleTotals.map(({ cycle, total }) => (
                <div
                  key={cycle}
                  className="d-flex align-items-center justify-content-between gap-3 px-3 pt-3"
                >
                  <div>
                    <div
                      className="text-uppercase fw-semibold"
                      style={{ letterSpacing: "0.06em", fontSize: "0.85rem" }}
                    >
                      {cycleTotals.length > 1 ? cycle : "Order total"}
                    </div>
                    <div
                      className="text-body-secondary"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {cycle === "Yearly"
                        ? "Charged today, then once a year"
                        : "Part of this month today, then on the 1st"}
                    </div>
                  </div>
                  <span className="fs-3 fw-bold">
                    {formatPriceUsd(total)}
                    <span className="text-body-secondary fw-normal fs-6">
                      {" "}
                      {cycle === "Yearly" ? "/yr" : "/mo"}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </CCardBody>
        )}
      </CCard>

      {lineCount > 0 && (
        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mt-3">
          <Link
            to={routePaths.strategies}
            className="btn btn-outline-secondary"
          >
            Continue browsing
          </Link>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            {includedLines.length > 0 && (
              <span className="small text-warning-emphasis">
                Remove {includedLines.map((item) => item.name).join(", ")} to check out.
              </span>
            )}
            <CButton color="primary" disabled={includedLines.length > 0} onClick={() => navigate("/checkout")}>
              Checkout
            </CButton>
          </div>
        </div>
      )}

      <CModal
        visible={pendingRemoval !== null}
        onClose={() => setPendingRemoval(null)}
        alignment="center"
      >
        <CModalHeader>
          <CModalTitle>Remove from cart?</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {pendingRemoval && (
            <p className="mb-0">
              Remove <strong>{pendingRemoval.name}</strong> from your cart? You
              can add it back any time.
            </p>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            variant="outline"
            onClick={() => setPendingRemoval(null)}
          >
            Cancel
          </CButton>
          <CButton color="danger" onClick={confirmRemove}>
            Remove
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}
